import React, { useState } from 'react'
import { Button } from '@wove/web-ui'
import { Plus, RefreshCw, AlertTriangle, Package } from 'lucide-react'
import { toast } from 'sonner'
import UnitTypesTable from './UnitTypesTable'
import UnitTypeForm from './UnitTypeForm'
import { useUnitTypes, useCreateUnitType, useUpdateUnitType, useDeleteUnitType, useRestIntegrations, UnitType, CreateUnitTypeRequest, UpdateUnitTypeRequest, Integration } from '@wove/api'

interface UnitTypesProps {
  integrationId?: string
}

export default function UnitTypes({ integrationId: providedIntegrationId }: UnitTypesProps) {
  const { data: integrations = [], isLoading: integrationsLoading, error: integrationsError } = useRestIntegrations()
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<string>('')

  // Use provided integration ID or manage selection locally
  const effectiveIntegrationId = providedIntegrationId || selectedIntegrationId

  // Set default integration when data loads (only if no integration provided)
  React.useEffect(() => {
    if (!providedIntegrationId && integrations.length > 0 && !selectedIntegrationId) {
      setSelectedIntegrationId(integrations[0].id)
    }
  }, [integrations, selectedIntegrationId, providedIntegrationId])

  const [showForm, setShowForm] = useState(false)
  const [editingUnitType, setEditingUnitType] = useState<UnitType | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<UnitType | null>(null)

  const {
    data: unitTypes = [],
    isLoading,
    error,
    refetch,
  } = useUnitTypes(effectiveIntegrationId)

  const createUnitTypeMutation = useCreateUnitType(effectiveIntegrationId)
  const updateUnitTypeMutation = useUpdateUnitType(effectiveIntegrationId)
  const deleteUnitTypeMutation = useDeleteUnitType(effectiveIntegrationId)

  const handleCreateUnitType = async (unitTypeData: CreateUnitTypeRequest) => {
    try {
      await createUnitTypeMutation.mutateAsync(unitTypeData)
      setShowForm(false)
      toast.success('Unit type created successfully')
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || 'Failed to create unit type')
    }
  }

  const handleUpdateUnitType = async (unitTypeData: UpdateUnitTypeRequest) => {
    if (!editingUnitType) return
    try {
      await updateUnitTypeMutation.mutateAsync({ id: editingUnitType.id, data: unitTypeData })
      setEditingUnitType(null)
      setShowForm(false)
      toast.success('Unit type updated successfully')
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || 'Failed to update unit type')
    }
  }

  const handleDeleteUnitType = async (unitType: UnitType) => {
    try {
      await deleteUnitTypeMutation.mutateAsync(unitType.id)
      setShowDeleteConfirm(null)
      toast.success('Unit type deleted successfully')
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || 'Failed to delete unit type')
    }
  }

  const openCreateForm = () => {
    setEditingUnitType(null)
    setShowForm(true)
  }

  const openEditForm = (unitType: UnitType) => {
    setEditingUnitType(unitType)
    setShowForm(true)
  }

  const closeForm = () => {
    setShowForm(false)
    setEditingUnitType(null)
  }

  // Handle integrations loading/error states (only if no integration provided)
  if (!providedIntegrationId) {
    if (integrationsError) {
      return (
        <div className="space-y-6" >
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Unit Types</h2>
            <p className="text-muted-foreground">
              Configure unit types for your integrations
            </p>
          </div>
          
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Error loading integrations
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{integrationsError.message}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }

    if (integrationsLoading) {
      return (
        <div className="space-y-6" >
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Unit Types</h2>
            <p className="text-muted-foreground">
              Configure unit types for your integrations
            </p>
          </div>
          
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading integrations...</span>
          </div>
        </div>
      )
    }

    if (integrations.length === 0) {
      return (
        <div className="space-y-6" >
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Unit Types</h2>
            <p className="text-muted-foreground">
              Configure unit types for your integrations
            </p>
          </div>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-yellow-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  No integrations found
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>You need to set up an integration before you can configure unit types.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }
  }

  if (error) {
    return (
      <div className="space-y-6" >
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Unit Types</h2>
          <p className="text-muted-foreground">
            Configure unit types for your integrations
          </p>
        </div>
        
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error loading unit types
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error.message}</p>
              </div>
              <div className="mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetch()}
                  className="text-red-700 border-red-300 hover:bg-red-100"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6" >
      {!providedIntegrationId && (
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Unit Types</h2>
          <p className="text-muted-foreground">
            Configure unit types for measurements, weight, and volume calculations
          </p>
        </div>
      )}

      {/* Integration Selector - only show when no integration provided */}
      {!providedIntegrationId && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex items-start">
            <Package className="h-5 w-5 text-blue-400 mt-0.5" />
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-blue-800">
                Integration Configuration
              </h3>
              <p className="mt-1 text-sm text-blue-700">
                Unit types are configured per integration. 
                {integrations.length === 1 
                  ? ` Currently managing unit types for: ${integrations[0].integrationSource || integrations[0].integrationType}`
                  : ' Select an integration to manage its unit types.'
                }
              </p>
              {integrations.length > 1 && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-blue-800 mb-1">
                    Select Integration:
                  </label>
                  <select
                    value={selectedIntegrationId}
                    onChange={(e) => setSelectedIntegrationId(e.target.value)}
                    className="px-3 py-2 border border-blue-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white min-w-64"
                  >
                    {integrations.map((integration: Integration) => (
                      <option key={integration.id} value={integration.id}>
                        {integration.integrationSource || integration.integrationType}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {integrations.length === 1 && (
                <div className="mt-2 text-xs text-blue-600">
                  Integration ID: {selectedIntegrationId}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-medium">Unit Type Definitions</h3>
          <span className="text-sm text-gray-500">
            {unitTypes.length} unit type{unitTypes.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={openCreateForm}
            disabled={isLoading}
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Unit Type
          </Button>
        </div>
      </div>

      {/* Unit Types Table */}
      <UnitTypesTable
        unitTypes={unitTypes}
        onEdit={openEditForm}
        onDelete={(unitType) => setShowDeleteConfirm(unitType)}
        isLoading={isLoading}
      />

      {/* Create/Edit Form Modal */}
      {showForm && (
        <UnitTypeForm
          unitType={editingUnitType || undefined}
          onSubmit={(data) => {
            if (editingUnitType) {
              handleUpdateUnitType(data as UpdateUnitTypeRequest)
            } else {
              handleCreateUnitType(data as CreateUnitTypeRequest)
            }
          }}
          onCancel={closeForm}
          isSubmitting={createUnitTypeMutation.isPending || updateUnitTypeMutation.isPending}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full m-4">
            <div className="p-6">
              <div className="flex items-center">
                <AlertTriangle className="h-6 w-6 text-red-600 mr-3" />
                <h3 className="text-lg font-medium text-gray-900">
                  Delete Unit Type
                </h3>
              </div>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete the unit type "{showDeleteConfirm.unitTypeName}"? 
                  This action cannot be undone.
                </p>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(null)}
                  disabled={deleteUnitTypeMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleDeleteUnitType(showDeleteConfirm)}
                  disabled={deleteUnitTypeMutation.isPending}
                >
                  {deleteUnitTypeMutation.isPending ? 'Deleting...' : 'Delete Unit Type'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}